import { Component, Inject, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommentsService } from '../../service/comments.service';
import { ExcelImportService, SchemaNode, SpreadsheetRows } from '../../service/excel-import.service';
import { SettingsService } from '../../service/settings.service';
import { ImportedTridy, TridysService } from '../../service/tridys.service';
import { TridyQrErrorCorrection, TridyQrExportService, TridyQrExportSettings, TridyQrFormat } from '../../service/tridy-qr-export.service';
import { insertQrFilenamePlaceholder, qrFilenameExtension, qrFilenameKeyLabel, qrFilenameKeyParent, qrFilenameSchemaKeys, qrFilenameTemplateError, QR_FILENAME_DATE_KEYS } from '../qr-filename-template';

interface ImportRow {
  rowNumber: number;
  payload: Record<string, unknown>;
  errors: string[];
}

@Component({
  selector: 'app-tridy-bulk-import-dialog',
  standalone: false,
  templateUrl: './tridy-bulk-import-dialog.component.html',
  styleUrls: ['./tridy-bulk-import-dialog.component.css']
})
export class TridyBulkImportDialogComponent {
  contentTypes: any[] = [];
  contentTypeId = '';
  contentTypeSearchTerm = '';
  spreadsheet?: SpreadsheetRows;
  rows: ImportRow[] = [];
  headerErrors: string[] = [];
  importing = false;
  importProgress = { completed: 0, total: 0 };
  generatingQr = false;
  qrProgress = { completed: 0, total: 0 };
  generateQrPackage = true;
  qrFormat: TridyQrFormat = 'cmyk-pdf';
  qrPrintSizeMm = 30;
  qrDpi = 300;
  qrQuietZoneModules = 4;
  qrErrorCorrection: TridyQrErrorCorrection = 'M';
  qrJpegQuality = 95;
  qrFilenamePrefix = 'dpp-qr-codes';
  filenameTemplate = '';
  readonly dateFilenameKeys = QR_FILENAME_DATE_KEYS;
  readonly filenameKeyLabel = qrFilenameKeyLabel;
  readonly filenameKeyParent = qrFilenameKeyParent;
  qrFilenameKey = '';
  qrChunkSize = 250;
  createdTridys: ImportedTridy[] = [];
  qrDownloadFailed = false;
  tridyCreateLimit = 1000;
  tridyCreatedToday?: number;
  checkingQuota = false;

  constructor(
    private dialogRef: MatDialogRef<TridyBulkImportDialogComponent>,
    private settingsService: SettingsService,
    private excelImportService: ExcelImportService,
    private tridysService: TridysService,
    private tridyQrExportService: TridyQrExportService,
    private commentsService: CommentsService,
    @Optional() @Inject(MAT_DIALOG_DATA) private data: any
  ) {
    dialogRef.disableClose = true;
    this.contentTypeId = data?.contentTypeId || '';
  }

  ngOnInit(): void {
    this.contentTypes = this.settingsService.settingsContentTypesWithId || [];
    if (!this.contentTypes.length) {
      this.settingsService.observeContextsContentTypesFirestoreWithId(this.settingsService.contextId$)
        .subscribe(contentTypes => this.contentTypes = contentTypes || []);
    }
    this.settingsService.selectedContextDataChange$.subscribe(() => this.refreshDailyTridyCreateAllowance());
    this.refreshDailyTridyCreateAllowance();
  }

  async readFile(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    try {
      this.spreadsheet = await this.excelImportService.readSpreadsheet(file);
      this.validateRows();
    } catch (error: any) {
      this.spreadsheet = undefined;
      this.rows = [];
      this.commentsService.addSnackBar.emit(error?.message || 'The Excel file could not be read.');
    } finally {
      input.value = '';
    }
  }

  validateRows(): void {
    if (!this.spreadsheet || !this.contentTypeId) { this.rows = []; this.headerErrors = []; return; }
    this.headerErrors = [];
    const schema = this.selectedContentType?.schema?.properties as SchemaNode[] | undefined;
    if (!schema?.length) {
      this.rows = this.spreadsheet.rows.map(row => ({ rowNumber: row.rowNumber, payload: {}, errors: ['This content type has no schema.'] }));
      this.refreshDailyTridyCreateAllowance();
      return;
    }
    const schemaKeys = this.excelImportService.getImportableSchemaKeys(schema);
    const unknownHeaders = this.spreadsheet.headers.filter(header => !schemaKeys.includes(header));
    this.headerErrors = unknownHeaders.length ? [`These columns are not part of the selected schema: ${unknownHeaders.join(', ')}.`] : [];
    this.rows = this.spreadsheet.rows.map(row => {
      const result = this.excelImportService.validatePayload(row.values, schema);
      return { rowNumber: row.rowNumber, payload: result.payload, errors: result.errors };
    });
    this.refreshDailyTridyCreateAllowance();
  }

  async importValidRows(): Promise<void> {
    const validRows = this.rows.filter(row => !row.errors.length);
    if (!this.contentTypeId || !this.spreadsheet || !validRows.length || this.importing || this.createdTridys.length) return;
    await this.refreshDailyTridyCreateAllowance();
    if (this.tridyCreatedToday === undefined) {
      this.commentsService.addSnackBar.emit('The daily Tridy creation allowance could not be verified. Please try again.');
      return;
    }
    if (this.remainingTridyCreations < validRows.length) {
      this.commentsService.addSnackBar.emit(`Daily Tridy creation limit reached. ${this.remainingTridyCreations} of ${validRows.length} requested records can still be created today.`);
      return;
    }
    this.importing = true;
    this.importProgress = { completed: 0, total: validRows.length };
    try {
      this.commentsService.progressSpin.emit(true);
      const created = await this.tridysService.createImportedTridys(
        this.contentTypeId,
        validRows.map(row => row.payload),
        this.spreadsheet.fileName,
        (completed, total) => this.importProgress = { completed, total }
      );
      this.createdTridys = created;
      this.commentsService.addSnackBar.emit(`${created.length} Tridy${created.length === 1 ? '' : 's'} generated.`);
      if (this.generateQrPackage) {
        try {
          await this.downloadQrPackage(created);
        } catch (error) {
          this.qrDownloadFailed = true;
          this.commentsService.addSnackBar.emit('DPP records were generated, but the QR ZIP could not be downloaded. Please retry.');
          return;
        }
      }
      this.dialogRef.close({ generated: true, count: created.length, contentTypeId: this.contentTypeId, tridyIds: created.map(tridy => tridy.id) });
    } catch (error: any) {
      this.commentsService.addSnackBar.emit(error?.message || 'The Tridys could not be generated.');
    } finally {
      this.commentsService.progressSpin.emit(false);
      this.importing = false;
    }
  }

  get selectedContentType(): any { return this.contentTypes.find(contentType => contentType.id === this.contentTypeId); }
  get filteredContentTypes(): any[] {
    const term = this.contentTypeSearchTerm.trim().toLowerCase();
    if (!term) return this.contentTypes;
    return this.contentTypes.filter(contentType =>
      String(contentType?.displayName || '').toLowerCase().includes(term)
        || String(contentType?.id || '').toLowerCase().includes(term)
    );
  }
  get validCount(): number { return this.headerErrors.length ? 0 : this.rows.filter(row => !row.errors.length).length; }
  get invalidCount(): number { return this.rows.length - this.validCount; }
  get previewRows(): ImportRow[] { return this.rows.slice(0, 8); }
  get qrFilenameKeys(): string[] {
    return qrFilenameSchemaKeys(this.selectedContentType?.schema);
  }
  get filenameTemplateError(): string | undefined { return qrFilenameTemplateError(this.filenameTemplate); }
  get filenameExtension(): string { return qrFilenameExtension(this.qrFormat); }
  insertFilenameKey(key: string, input: HTMLInputElement): void {
    this.filenameTemplate = insertQrFilenamePlaceholder(this.filenameTemplate, key, input);
  }
  get recommendedQrPixels(): number {
    return Math.round((this.qrPrintSizeMm / 25.4) * this.qrDpi);
  }
  get isPdfExport(): boolean { return this.qrFormat === 'cmyk-pdf'; }
  get remainingTridyCreations(): number {
    return Math.max(0, this.tridyCreateLimit - (this.tridyCreatedToday || 0));
  }
  get quotaMessage(): string {
    if (this.checkingQuota || this.tridyCreatedToday === undefined) return 'Checking today’s Tridy creation allowance…';
    return `${this.remainingTridyCreations} of ${this.tridyCreateLimit} Tridys remaining today.`;
  }
  get importPercent(): number {
    if (!this.importProgress.total) return 0;
    return Math.round((this.importProgress.completed / this.importProgress.total) * 100);
  }
  get importDisabledReason(): string | undefined {
    if (this.importing) return 'DPP records are currently being created.';
    if (this.generatingQr) return 'QR codes are currently being generated.';
    if (this.createdTridys.length) return 'These rows have already generated DPP records. Use the QR retry button if needed.';
    if (this.generateQrPackage && this.filenameTemplateError) return this.filenameTemplateError;
    if (!this.contentTypeId) return 'Choose a content type first.';
    if (!this.spreadsheet) return 'Choose an Excel, CSV, or XLS file to continue.';
    if (this.checkingQuota || this.tridyCreatedToday === undefined) return 'The daily Tridy creation allowance is being checked.';
    if (this.validCount > this.remainingTridyCreations) return `This generation exceeds today’s remaining allowance of ${this.remainingTridyCreations} Tridys.`;
    if (this.headerErrors.length) return 'Fix the spreadsheet columns so they match the selected schema.';
    if (!this.validCount) return 'No valid rows are ready to generate. Review the data-check issues above.';
    return undefined;
  }

  close(): void {
    if (!this.importing && !this.generatingQr) this.dialogRef.close(false);
  }

  async retryQrDownload(): Promise<void> {
    if (!this.createdTridys.length || this.generatingQr) return;
    try {
      await this.downloadQrPackage(this.createdTridys);
      this.qrDownloadFailed = false;
      this.dialogRef.close({ generated: true, count: this.createdTridys.length, contentTypeId: this.contentTypeId, tridyIds: this.createdTridys.map(tridy => tridy.id) });
    } catch (error) {
      this.commentsService.addSnackBar.emit('The QR ZIP could not be downloaded. Please try again.');
    }
  }

  private async downloadQrPackage(created: ImportedTridy[]): Promise<void> {
    this.generatingQr = true;
    this.qrDownloadFailed = false;
    this.qrProgress = { completed: 0, total: created.length };
    try {
      const settings: TridyQrExportSettings = {
        format: this.qrFormat,
        widthMm: Math.max(10, Number(this.qrPrintSizeMm) || 30),
        dpi: Math.max(72, Number(this.qrDpi) || 300),
        errorCorrection: this.qrErrorCorrection,
        quietZoneModules: Math.max(4, Number(this.qrQuietZoneModules) || 4),
        jpegQuality: Math.max(1, Number(this.qrJpegQuality) || 95),
        filenamePrefix: this.qrFilenamePrefix,
        filenameTemplate: this.filenameTemplate,
      };
      await this.tridyQrExportService.downloadFromBackend(created, settings);
      this.qrProgress = { completed: created.length, total: created.length };
      this.commentsService.addSnackBar.emit('QR code download started.');
    } finally {
      this.generatingQr = false;
    }
  }

  private async refreshDailyTridyCreateAllowance(): Promise<void> {
    const configuredLimit = Number(this.settingsService.contextAccount$?.limits?.maxTridyCreateCount);
    this.tridyCreateLimit = Number.isFinite(configuredLimit) && configuredLimit >= 0 ? configuredLimit : 1000;
    if (!this.settingsService.contextId$) return;
    this.checkingQuota = true;
    try {
      this.tridyCreatedToday = await this.tridysService.getTodayTridyCreationCount();
    } catch {
      // Do not allow an import until today's count can be verified.
      this.tridyCreatedToday = undefined;
    } finally {
      this.checkingQuota = false;
    }
  }

}
